'use client';

import { forwardRef, useEffect, useState, useSyncExternalStore } from 'react';
import { cn } from '@/lib/utils';
import { type StatusImageProps } from './status-image.types';

type StatusImageSets = typeof import('./status-images');

// A media query, unlike srcset's density hint, is binding: Chrome takes any
// srcset candidate already in memory, which inline data always is, so a 2x
// screen would downscale the 3x file.
const HIGH_DENSITY =
  '(min-resolution: 2.5dppx), (-webkit-min-device-pixel-ratio: 2.5)';

const BOX_SIZE = { 20: 'h-5 w-5', 30: 'h-[30px] w-[30px]' } as const;

let loadedSets: StatusImageSets | null = null;
let setsRequest: Promise<void> | null = null;
const setsListeners = new Set<() => void>();

function subscribeToSets(listener: () => void) {
  setsListeners.add(listener);
  return () => {
    setsListeners.delete(listener);
  };
}

const getLoadedSets = () => loadedSets;
const getServerSets = () => null;

function loadSets() {
  setsRequest ??= import('./status-images').then((sets) => {
    loadedSets = sets;
    setsListeners.forEach((listener) => listener());
  });
}

function sourcesFor(
  sets: StatusImageSets,
  props: StatusImageProps
): [base: string, dense: string] {
  return props.size === 20
    ? [sets.STATUS_IMAGE_40[props.variant], sets.STATUS_IMAGE_60[props.variant]]
    : [sets.STATUS_IMAGE_60[props.variant], sets.STATUS_IMAGE_90[props.variant]];
}

/**
 * The 3D status icon at 20px (alerts) or 30px (toasts): the 2x file by
 * default, the 3x file at 2.5 dppx and above. The images load as one chunk
 * the first time any StatusImage mounts; until then it holds an empty box of
 * the same size, and an icon that had to wait fades in.
 *
 * @example
 * ```tsx
 * <StatusImage size={30} variant="success" />
 * ```
 */
const StatusImage = forwardRef<HTMLPictureElement, StatusImageProps>(
  (props, ref) => {
    const { size, className } = props;
    const sets = useSyncExternalStore(
      subscribeToSets,
      getLoadedSets,
      getServerSets
    );
    const [hasWaited] = useState(!sets);

    useEffect(loadSets, []);

    const sources = sets && sourcesFor(sets, props);

    return (
      <picture
        ref={ref}
        className={cn('block shrink-0', BOX_SIZE[size], className)}
      >
        {sources && (
          <>
            <source media={HIGH_DENSITY} srcSet={sources[1]} />
            <img
              src={sources[0]}
              width={size}
              height={size}
              alt=""
              aria-hidden="true"
              draggable={false}
              className={cn(
                'block select-none',
                BOX_SIZE[size],
                hasWaited && 'animate-icon-in'
              )}
            />
          </>
        )}
      </picture>
    );
  }
);

StatusImage.displayName = 'StatusImage';

export { StatusImage };
